// WishlistGridPublic.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { FaGift } from 'react-icons/fa';

const WishlistGridPublic = ({ id }) => {
  const [allItems, setAllItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const itemsPerLoad = 12;
  const observer = useRef();

  useEffect(() => {
    fetchWishlist();
  }, [id]);

  const fetchWishlist = () => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/wishlists/${id}`)
      .then((res) => {
        const sorted = res.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setAllItems(sorted);
        setDisplayedItems(sorted.slice(0, itemsPerLoad));
        setHasMore(sorted.length > itemsPerLoad);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching wishlist:', err);
        setLoading(false);
      });
  };

  const loadMore = () => {
    const nextItems = allItems.slice(displayedItems.length, displayedItems.length + itemsPerLoad);
    setDisplayedItems(prev => [...prev, ...nextItems]);
    if (displayedItems.length + nextItems.length >= allItems.length) {
      setHasMore(false);
    }
  };

  const lastItemRef = useCallback(node => {
    if (loading || !hasMore) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        loadMore();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore, displayedItems]);

  if (loading) {
    return <div className="text-center py-10 text-gray-500">Loading wishlist...</div>;
  }

  if (displayedItems.length === 0) {
    return (
      <div className="text-center py-20 text-gray-600">
        <FaGift className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-4 text-lg font-medium">No wishlist items added yet.</p>
        <p className="text-sm text-gray-500">Check back later to see updates.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold my-4 text-center">Wishlist</h2>
      <div className="grid grid-cols-2 gap-4">
        {displayedItems.map((item, index) => (
          <div
            key={item.id}
            ref={index === displayedItems.length - 1 ? lastItemRef : null}
            className="border rounded p-4 shadow-sm hover:shadow-md transition relative"
          >
            {item.image_url && (
              <img
                src={item.image_url}
                alt={item.title}
                className="w-full aspect-square object-cover mb-2 rounded"
              />
            )}
            <h3 className="font-semibold text-md mb-1 line-clamp-2">{item.title}</h3>
            {item.url && (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline text-sm"
              >
                View item
              </a>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default WishlistGridPublic;
